import remarkMath from 'remark-math'
import remarkParse from 'remark-parse'
import { unified } from 'unified'

const parser = unified().use(remarkParse).use(remarkMath)
const scientificAsterisk = /(?<=[\p{L}\p{N}])\*(?=\d)/gu

/** Keep intraword scientific notation such as Aβ*56 literal in prose.
 * Code, math, HTML and link destinations retain their original syntax.
 */
export function escapeScientificAsterisks(markdown: string): string {
  const matches = [...markdown.matchAll(scientificAsterisk)]
  if (!matches.length) return markdown

  const protectedRanges: [number, number][] = []
  type Node = { type: string; position?: { start: { offset?: number }; end: { offset?: number } }; children?: Node[] }
  const visit = (node: Node) => {
    if (node.type === 'link') {
      const start = node.position?.start.offset
      const end = node.position?.end.offset
      const labelEnd = node.children?.at(-1)?.position?.end.offset
      // Bracketed link labels are prose; their destination and title are syntax.
      // Autolinks are entirely URLs and should remain untouched.
      if (start !== undefined && end !== undefined) {
        protectedRanges.push([markdown[start] === '[' ? (labelEnd ?? start) : start, end])
      }
      if (start !== undefined && markdown[start] === '[') {
        for (const child of node.children ?? []) visit(child)
      }
      return
    }
    if (['code', 'inlineCode', 'math', 'inlineMath', 'html', 'image', 'definition'].includes(node.type)) {
      const start = node.position?.start.offset
      const end = node.position?.end.offset
      if (start !== undefined && end !== undefined) protectedRanges.push([start, end])
      return
    }
    for (const child of node.children ?? []) visit(child)
  }
  visit(parser.parse(markdown))

  let result = markdown
  for (const match of matches.reverse()) {
    const offset = match.index
    if (protectedRanges.some(([start, end]) => offset >= start && offset < end)) continue
    result = result.slice(0, offset) + '\\' + result.slice(offset)
  }
  return result
}
